import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { MONTHS, DEMO_HEX } from "@/lib/services";
import { LogoMark } from "@/components/app/Logo";

const PATTERN = ["poco", "bebedouro", "caixa", "contrato", "caixa", "bebedouro", "contrato", "poco", "bebedouro", "caixa", "contrato", "bebedouro"];

const YearArt = () => (
  <div className="grid grid-cols-4 gap-2.5">
    {MONTHS.map((m, i) => (
      <div key={m} className="rise rounded-lg border border-slate-700/60 bg-slate-800/70 p-2.5" style={{ animationDelay: `${i * 50}ms` }}>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{m.slice(0, 3)}</div>
        <div className="mt-2 space-y-1">
          <div className="h-1.5 rounded-full" style={{ background: DEMO_HEX[PATTERN[i]], width: `${55 + ((i * 17) % 40)}%` }} />
          <div className="h-1.5 w-1/2 rounded-full bg-slate-700" />
        </div>
      </div>
    ))}
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-grain grid min-h-screen lg:grid-cols-2">
      <div className="hidden flex-col justify-between border-r border-slate-800 p-12 lg:flex">
        <div className="flex items-center gap-2 font-display text-xl font-extrabold tracking-tight">
          <LogoMark className="h-8 w-8" /> Ciclo Certo
        </div>
        <div className="max-w-md">
          <YearArt />
          <p className="mt-8 font-display text-3xl font-bold leading-tight tracking-tight">
            Cada serviço feito já deixa o próximo agendado.
          </p>
          <p className="mt-3 text-sm text-slate-400">Poço, bebedouro, caixa e contratos com renovação automática pela validade.</p>
        </div>
        <div className="text-xs text-slate-500">Gestão de clientes e serviços recorrentes</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="rise w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <LogoMark className="mb-6 h-12 w-12 lg:hidden" />
            <h1 className="font-display text-4xl font-extrabold tracking-tight">Entrar</h1>
            <p className="mt-2 text-sm text-slate-400">Use o e-mail e a senha da sua conta.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="h-11 bg-slate-900" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Senha</Label>
              <Link to="/esqueci-senha" className="text-xs text-slate-400 underline hover:text-slate-200" data-testid="login-forgot-link">Esqueci minha senha</Link>
            </div>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="h-11 bg-slate-900" />
          </div>
          {error && <p className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="h-11 w-full rounded-full font-semibold" data-testid="login-submit-button">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Entrar
          </Button>
          <p className="text-center text-sm text-slate-400">Ainda não tem conta? <Link to="/cadastro" className="font-semibold text-slate-100 underline" data-testid="login-signup-link">Teste grátis por 7 dias</Link></p>
        </form>
      </div>
    </div>
  );
}
